import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Link,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
  FaBrain,
  FaBriefcase,
  FaCode,
  FaInfoCircle,
  FaLayerGroup,
} from "react-icons/fa";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../../logo.png";

export default function Navbar() {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const location = useLocation();

  const navLinks = [
    { label: "Services", path: "/services", icon: FaLayerGroup },
    { label: "Our work", path: "/portfolio", icon: FaLayerGroup },
    { label: "How we build", path: "/technologies", icon: FaBrain },
    { label: "About", path: "/about", icon: FaInfoCircle },
    { label: "Careers", path: "/careers", icon: FaBriefcase },
  ];

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={1000}
      bg="rgba(8, 13, 24, 0.78)"
      borderBottom="1px solid rgba(0, 255, 208, 0.18)"
      sx={{
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        boxShadow: "0 14px 42px rgba(0, 0, 0, 0.26)",
      }}
    >
      <Link
        href="#main-content"
        position="fixed"
        top={2}
        left={2}
        zIndex={1100}
        bg="#00FFD0"
        color="#081018"
        px={4}
        py={2}
        borderRadius="md"
        fontWeight="bold"
        transform="translateY(-160%)"
        _focus={{ transform: "translateY(0)" }}
      >
        Skip to content
      </Link>
      <Flex
        as="nav"
        align="center"
        justify="space-between"
        maxW="1440px"
        mx="auto"
        px={{ base: 4, md: 6, xl: 8 }}
        py={3}
        gap={4}
      >
        <Link
          as={RouterLink}
          to="/"
          display="flex"
          alignItems="center"
          gap={3}
          _hover={{ textDecoration: "none" }}
          onClick={onClose}
        >
          <Image
            src={logo}
            alt="Orvitrix home"
            objectFit="contain"
            w={{ base: "92px", md: "106px" }}
            h="54px"
            filter="drop-shadow(0 0 10px rgba(255, 111, 32, 0.44))"
          />
          <Badge
            display={{ base: "none", lg: "inline-flex" }}
            bg="rgba(0, 255, 208, 0.1)"
            color="#00FFD0"
            border="1px solid rgba(0, 255, 208, 0.24)"
            borderRadius="full"
            px={3}
            py={1}
          >
            Product Studio
          </Badge>
        </Link>

        <HStack
          spacing={1}
          display={{ base: "none", lg: "flex" }}
          bg="rgba(255, 255, 255, 0.045)"
          border="1px solid rgba(255, 255, 255, 0.08)"
          borderRadius="full"
          p={1}
        >
          {navLinks.map((link) => {
            const active = isActive(link.path);
            return (
              <Link
                key={link.path}
                as={RouterLink}
                to={link.path}
                display="flex"
                alignItems="center"
                gap={2}
                px={3}
                py={2}
                borderRadius="full"
                color={active ? "#081018" : "#F4F4F6"}
                bg={active ? "#00FFD0" : "transparent"}
                fontWeight="bold"
                fontSize="sm"
                whiteSpace="nowrap"
                aria-current={active ? "page" : undefined}
                _hover={{
                  color: active ? "#081018" : "#00FFD0",
                  bg: active ? "#00FFD0" : "rgba(0, 255, 208, 0.1)",
                  textDecoration: "none",
                }}
              >
                <Icon as={link.icon} boxSize={3.5} />
                {link.label}
              </Link>
            );
          })}
        </HStack>

        <HStack spacing={3} display={{ base: "none", lg: "flex" }}>
          <Button
            as={RouterLink}
            to="/contact"
            size="sm"
            bg="#FF6F20"
            color="white"
            rightIcon={<FaCode />}
            borderRadius="full"
            px={5}
            _hover={{
              bg: "#ff853d",
              boxShadow: "0 0 18px rgba(255, 111, 32, 0.42)",
              transform: "translateY(-1px)",
            }}
          >
            Start a project
          </Button>
        </HStack>

        <IconButton
          icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
          aria-label="Toggle menu"
          display={{ base: "inline-flex", lg: "none" }}
          onClick={onToggle}
          variant="ghost"
          color="#F4F4F6"
          border="1px solid rgba(255, 255, 255, 0.12)"
          borderRadius="lg"
          _hover={{ color: "#00FFD0", bg: "rgba(0, 255, 208, 0.08)" }}
        />
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <motion.div
          initial={{ y: -12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -12, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Box px={4} pb={4} display={{ lg: "none" }}>
            <VStack
              align="stretch"
              spacing={2}
              bg="rgba(15, 23, 42, 0.96)"
              border="1px solid rgba(255, 255, 255, 0.1)"
              borderRadius="xl"
              p={3}
            >
              {navLinks.map((link) => {
                const active = isActive(link.path);
                return (
                  <Link
                    key={link.path}
                    as={RouterLink}
                    to={link.path}
                    display="flex"
                    alignItems="center"
                    gap={3}
                    px={4}
                    py={3}
                    borderRadius="lg"
                    color={active ? "#081018" : "#F4F4F6"}
                    bg={active ? "#00FFD0" : "transparent"}
                    fontWeight="bold"
                    aria-current={active ? "page" : undefined}
                    _hover={{
                      color: active ? "#081018" : "#00FFD0",
                      bg: active ? "#00FFD0" : "rgba(0, 255, 208, 0.1)",
                      textDecoration: "none",
                    }}
                    onClick={onClose}
                  >
                    <Icon as={link.icon} boxSize={4} />
                    <Text>{link.label}</Text>
                  </Link>
                );
              })}
              <Button
                as={RouterLink}
                to="/contact"
                bg="#FF6F20"
                color="white"
                rightIcon={<FaCode />}
                borderRadius="lg"
                onClick={onClose}
                _hover={{ bg: "#ff853d" }}
              >
                Start a project
              </Button>
            </VStack>
          </Box>
        </motion.div>
      </Collapse>
    </Box>
  );
}
